import { ActionResult, ACTION_STATE } from "js/actions/actions";
import { NEW_RECORD_GUID_PREFIX } from "js/sheet-view/grid/grid-selection.js";

const selectionHelpers = {
	moveRelatively(vueComponent, displayRecords, sortedFields, gridSelection, relativeRecordGUID, relativeFieldGUID, locationChangeVector, shouldHighlight, allowNewRecordCreation, highlightRangeChangeInitiator) {
		// Default variables necessary for the duration of the function
		const dataStore = vueComponent.$root.systemState.applicationData.activeWorkbook.dataStore;
		// Start off by fetching a list of all of the available records and field guids so we have a full picture of the viewable cells
		const listOfAllRecordGUIDs = displayRecords.filter(function isRecord({ record, type }) {
			return record !== null; // Filter our null records
		}).map(function resolveGUID({ record }) {
			return record.$internals.metaProperties.meta.guid; // Return just the guid, that's all we care about
		});
		const listOfAllFieldGUIDs = sortedFields.filter(function isField(fieldStruct) {
			return fieldStruct.type === "field"; // We just care about fields
		}).map(function resolveGUID(fieldStruct) {
			return fieldStruct.key; // And we only care about the key
		});
		// Find the index of the relative GUIDs so that we can start making selections/highlights as necessary
		let indexOfRelativeRecordGUID;
		if (relativeRecordGUID === NEW_RECORD_GUID_PREFIX) {
			indexOfRelativeRecordGUID = listOfAllRecordGUIDs.length;
		} else if (relativeRecordGUID.indexOf(`${NEW_RECORD_GUID_PREFIX}-`) === 0) {
			indexOfRelativeRecordGUID = listOfAllRecordGUIDs.indexOf(relativeRecordGUID.substring(`${NEW_RECORD_GUID_PREFIX}-`.length));
		} else {
			indexOfRelativeRecordGUID = listOfAllRecordGUIDs.indexOf(relativeRecordGUID);
		}
		let indexOfRelativeFieldGUID = listOfAllFieldGUIDs.indexOf(relativeFieldGUID);
		// Figure out the target location indices
		const indexOfTargetRecordGUID = Math.min(listOfAllRecordGUIDs.length - (allowNewRecordCreation ? 0 : 1), Math.max(0, indexOfRelativeRecordGUID + locationChangeVector.rows));
		const indexOfTargetFieldGUID = Math.min(listOfAllFieldGUIDs.length - (allowNewRecordCreation ? 0 : 1), Math.max(0, indexOfRelativeFieldGUID + locationChangeVector.columns));
		// Close all pops that are open, if we are navigating, lets not have the pops in the way
		vueComponent.$pubsub.modules.dataStoreCellMessage.pub("closeAll", {});
		/**
		 * SHOT: WHY THE FUCK ARE WE WRAPPING THIS IN A SETTIMEOUT? Makes legit zero sense...right?
		 * Well, in Vue, we use setTimeout's a lot when we want the render "thread" to catch up, which is exactly what we want when this function is called repeatedly
		 * Consider a user arrowing-down on the grid. If the user presses it once, below code is executed and selection/grid is updated instantly. If the user holds
		 * it down, these pubsubs are all called repeatedly until the user releases the key. That sucks, because that means the render "thread" doesn't catch up
		 * until after the user releases the arrow key, which is shitty, because they wont to see the selection moving as they are holding down the key. The settimeout
		 * here allows for the render "thread" to catchup while the key is held down.
		 * 
		 * We're basically slowing down our code to give a faster user experience... wack af
		 */
		setTimeout(function afterTimeout() {
			// This is where we begin to diverge functionality. If we simply want to select, do the following
			if (!shouldHighlight) {
				// Verify the range of the selection
				if (indexOfTargetRecordGUID < listOfAllRecordGUIDs.length && indexOfTargetFieldGUID < listOfAllFieldGUIDs.length) {
					// In this instance, we are within range, so just select
					vueComponent.$pubsub.modules.tableNavigation.pub("selectRecordField", {
						recordGUID: listOfAllRecordGUIDs[indexOfTargetRecordGUID],
						fieldGUID: listOfAllFieldGUIDs[indexOfTargetFieldGUID]
					});
				} else if (indexOfTargetRecordGUID === listOfAllRecordGUIDs.length && dataStore.hasPermission("record_addNew")) {
					// In this instance, we are at the last row, and want to go down, which means creating a new row
					vueComponent.$pubsub.modules.tableNavigation.pub("selectRecordField", {
						recordGUID: NEW_RECORD_GUID_PREFIX,
						fieldGUID: listOfAllFieldGUIDs[indexOfTargetFieldGUID]
					});
					vueComponent.$nextTick(function afterTick() {
						vueComponent.$pubsub.modules.tableNavigation.pub("addNewRecordAndFocus", {
							fieldGUID: listOfAllFieldGUIDs[indexOfTargetFieldGUID]
						})
					});
				} else if (relativeRecordGUID === NEW_RECORD_GUID_PREFIX && indexOfTargetFieldGUID > 0 && indexOfTargetFieldGUID < listOfAllFieldGUIDs.length) {
					// In this instance, just move off the new cell
					vueComponent.$pubsub.modules.tableNavigation.pub("selectRecordField", {
						recordGUID: listOfAllRecordGUIDs[listOfAllRecordGUIDs.length - 1],
						fieldGUID: listOfAllFieldGUIDs[indexOfTargetFieldGUID]
					});
				} else {
					// If none of the previous scenarios were seen, just stay where we are. Note that this needs to be explicitly set to allow for navigation off of the grid to still count as a save (etc: enter on the last row)
					vueComponent.$pubsub.modules.tableNavigation.pub("selectRecordField", {
						recordGUID: relativeRecordGUID,
						fieldGUID: relativeFieldGUID
					});
				}
			} else {
				// Get a handle to the currently selected range
				const highlightedRange = gridSelection.highlightedRanges[0] || {};
				const currentHighlightedRecordGUIDs = highlightedRange.recordGUIDs;
				const currentHighlightedFieldGUIDs = highlightedRange.fieldGUIDs;
				// Find the current highlight indices
				const indexOfStartRangeRecordGUID = listOfAllRecordGUIDs.indexOf(currentHighlightedRecordGUIDs[0]);
				const indexOfStartRangeFieldGUID = listOfAllFieldGUIDs.indexOf(currentHighlightedFieldGUIDs[0]);
				const indexOfEndRangeRecordGUID = listOfAllRecordGUIDs.indexOf(currentHighlightedRecordGUIDs[currentHighlightedRecordGUIDs.length - 1]);
				const indexOfEndRangeFieldGUID = listOfAllFieldGUIDs.indexOf(currentHighlightedFieldGUIDs[currentHighlightedFieldGUIDs.length - 1]);
				// Calculate the current highlight range size
				const currentHighlightRecordSize = (indexOfEndRangeRecordGUID - indexOfStartRangeRecordGUID) * (indexOfRelativeRecordGUID === indexOfEndRangeRecordGUID ? -1 : 1);
				const currentHighlightFieldSize = (indexOfEndRangeFieldGUID - indexOfStartRangeFieldGUID) * (indexOfRelativeFieldGUID === indexOfEndRangeFieldGUID ? -1 : 1);
				// Calculate the target highlight range size
				const targetHighlightRecordSize = currentHighlightRecordSize + locationChangeVector.rows;
				const targetHighlightFieldSize = currentHighlightFieldSize + locationChangeVector.columns;
				// Find the new highlight start index
				const indexOfHighlightStartRecord = Math.max(0, Math.min(indexOfRelativeRecordGUID, indexOfRelativeRecordGUID + targetHighlightRecordSize));
				const indexOfHighlightStartField = Math.max(0, Math.min(indexOfRelativeFieldGUID, indexOfRelativeFieldGUID + targetHighlightFieldSize));
				// Find the new highlight end index
				const indexOfHighlightEndRecord = Math.min(listOfAllRecordGUIDs.length, Math.max(indexOfRelativeRecordGUID, indexOfRelativeRecordGUID + targetHighlightRecordSize) + 1);
				const indexOfHighlightEndField = Math.min(listOfAllFieldGUIDs.length, Math.max(indexOfRelativeFieldGUID, indexOfRelativeFieldGUID + targetHighlightFieldSize) + 1);
				// Construct the proper range array
				const recordRange = listOfAllRecordGUIDs.slice(indexOfHighlightStartRecord, indexOfHighlightEndRecord);
				const fieldRange = listOfAllFieldGUIDs.slice(indexOfHighlightStartField, indexOfHighlightEndField);
				// Execute the selection
				gridSelection.clearHighlight();
				gridSelection.highlightRange(recordRange, fieldRange, highlightRangeChangeInitiator);
			}
		});
	}
}
const nestingHelpers = {
	setNestingVisibility(vueComponent, records, visible) {
		// Grab a handle to our sheetView
		const sheetView = vueComponent.$root.systemState.applicationData.activeWorkbook.sheetView;
		const nestingOpenRecords = (sheetView.getNestingDisclosureArray() || []).reduce(function toMap(previousValue, currentValue) {
			return {
				...previousValue,
				[currentValue]: true
			}
		}, {});
		// Add parent records as necessary
		let parentRecords = {};
		for (let i = 0; i < records.length; i++) {
			let parent = records[i].getParentRecord();
			if (parent !== null && (!records[i].hasChildren() || !nestingOpenRecords[records[i].$internals.metaProperties.meta.guid]) && parent.$internals.metaProperties.meta.guid !== records[i].$internals.metaProperties.meta.guid) {
				parentRecords[parent.$internals.metaProperties.meta.guid] = parent;
			}
		}
		Object.keys(parentRecords).forEach(function addToRecords(guid) {
			records.push(parentRecords[guid]);
		});
		let closedRecords = {};
		let openRecords = {};
		// Loop through all of the records we need to, and ensure that they are in the state we want them to be in
		for (let i = 0; i < records.length; i++) {
			let record = records[i];
			if (visible) {
				sheetView.discloseChildrenOfRecord(record);
				if (!parentRecords[record.$internals.metaProperties.meta.guid]) {
					openRecords[record.$internals.metaProperties.meta.guid] = record;
				}
			} else {
				sheetView.hideChildrenOfRecord(record);
				if (!parentRecords[record.$internals.metaProperties.meta.guid]) {
					closedRecords[record.$internals.metaProperties.meta.guid] = record;
				}
			}
		}
		// Return information back to anyone who cares
		return {
			originallyOpenRecords: nestingOpenRecords,
			recordsOpened: openRecords,
			recordsClosed: closedRecords,
		};
	},
	setNestingVisibilityByLevel(vueComponent, recordsByLevel, levelsInfo, level) {
		// Grab a handle to our sheetView
		const sheetView = vueComponent.$root.systemState.applicationData.activeWorkbook.sheetView;
		// Calculate all of the records that need to be opened
		let recordsToOpen = [];
		let recordsOpen = [];
		for (let j = 0; j <= Math.min(level, levelsInfo.rootLevel + levelsInfo.depth); j++) {
			if (recordsByLevel[j]) { // Last minute check to ensure that this object instance exists
				if (j < Math.min(level, levelsInfo.rootLevel + levelsInfo.depth)) {
					recordsToOpen.push(...recordsByLevel[j].recordsAtLevel);
				}
				recordsOpen.push(...recordsByLevel[j].recordsAtLevel);
			}
		}
		// Resolve each record to its guid
		const guidMap = {};
		recordsToOpen = recordsToOpen.map(function toGUID(current) {
			guidMap[current.$internals.metaProperties.meta.guid] = current;
			return current.$internals.metaProperties.meta.guid;
		});
		recordsOpen = recordsOpen.reduce(function toMap(previousValue, currentValue) {
			return {
				...previousValue,
				[currentValue.$internals.metaProperties.meta.guid]: currentValue
			}
		}, {});
		// Update the display hierarchy object
		sheetView.setNestingDisclosureArray(recordsToOpen);
		return recordsOpen;
	}
}

export default {
	/**
	 * Update a particular cell value
	 */
	changeRecordFieldValue: {
		/**
		 * Update a particular cell value
		 * @param {Object} record Record row
		 * @param {String} fieldGUID Column id
		 * @param {any} fieldVal Any value to be stored in the particular cell
		 */
		execute(record, fieldGUID, fieldVal) {
			// Get a handle to the previous value before we do anything
			let previousValue = record.fieldValues[fieldGUID] ? record.fieldValues[fieldGUID].value : null; // Note: here, we go directly to the fieldValue (instead of using getFieldValue) since we need to store the raw data
			// Actually execute the change field action
			record.changeField(fieldGUID, fieldVal);
			// Grab the latest value for confirmation
			let newValue = record.fieldValues[fieldGUID].value;
			// Return our action result
			return new ActionResult(newValue === fieldVal ? ACTION_STATE.EXECUTED : ACTION_STATE.EXECUTE_FAILED, function canRevert() {
				return newValue === fieldVal;
			}, record, fieldGUID, previousValue);
		},
		// The inverse of this function is itself this function
		revert: "changeRecordFieldValue"
	},
	/**
	 * Update the current view to a particular sheet view
	 */
	changeSheetView: {
		/**
		 * Change to a different sheetview
		 * @param {String} nextSheetViewGUID SheetView GUID of the sheet view to switch to
		 * @returns {ActionResult} Execution and reversion details for this particular action
		 */
		execute(nextSheetViewGUID) {
			const dataStoreGUID = this.$root.systemState.applicationData.activeWorkbook.dataStore.$internals.metaProperties.meta.guid;
			const currentSheetViewGUID = this.$root.systemState.applicationData.activeWorkbook.sheetView.$internals.metaProperties.meta.guid;
			this.$router.push(`/sheets/${dataStoreGUID}/${nextSheetViewGUID}`);
			return new ActionResult(ACTION_STATE.EXECUTED, function canRevert() {
				return true;
			}, currentSheetViewGUID)
		},
		revert: "changeSheetView"
	},
	/**
	 * Toggle the nesting visibility
	 */
	toggleNestingVisibility: {
		/**
		 * Toggle the nesting visibility of a particular row
		 * @param {GridRecordDisplay} rowData Row display data, which includes more than just the record
		 * @returns {ActionResult} Execution and reversion details for this particular action
		 */
		execute(rowData) {
			// Grab a handle to the root sheetview
			const sheetView = this.$root.systemState.applicationData.activeWorkbook.sheetView;
			if (rowData.childsDisclosureTriangleShowsRecordChildren) { // If we are open, close
				sheetView.hideChildrenOfRecord(rowData.record);
			} else { // If we are closed, open
				sheetView.ensureRecordChildrenVisibleInHierarchy(rowData.record);
			}
			// The above functions don't actually reset this value :(, but we still need to set it for the revert functionality
			rowData.childsDisclosureTriangleShowsRecordChildren = !rowData.childsDisclosureTriangleShowsRecordChildren;
			return new ActionResult(ACTION_STATE.EXECUTED, function canRevert() {
				return true;
			}, rowData);
		},
		revert: "toggleNestingVisibility"
	},
	/**
	 * Set the particular nesting visibility
	 */
	changeNestingVisibility: {
		/**
		 * Set the particular nesting visibility of a set of records
		 * @param {GridSelection} gridSelection Global grid selection object
		 * @param {List} records List of records to set the visibility to
		 * @param {Boolean} visible Set the visbility levels
		 * @returns {ActionResult} Execution and reversion details for this particular action
		 */
		execute(gridSelection, records, visible) {
			// Set the visibility state accordingly
			const nestingVisibilityResult = nestingHelpers.setNestingVisibility(this, records, visible);
			// Grab our current selections
			const selectedCell = gridSelection && gridSelection.selectedCell ? gridSelection.selectedCell : null;
			const selectedRecordGUID = selectedCell ? selectedCell.recordGUID : null;
			const selectedFieldGUID = selectedCell ? selectedCell.fieldGUID : null;
			// If we are in a collapsing state, we may run into the edge case where nothing is selected anymore. Therefore, check to see if we have anything in the grid selected, and if not, attempt to select the most logical option available
			if (!visible && gridSelection) {
				if (!!selectedRecordGUID && (!gridSelection.selectedCell || nestingVisibilityResult.recordsClosed[gridSelection.selectedCell.recordGUID])) {
					let currentGUID = selectedRecordGUID;
					while (currentGUID && nestingVisibilityResult.recordsClosed[currentGUID] && (!nestingVisibilityResult.recordsClosed[currentGUID].hasChildren() || !nestingVisibilityResult.originallyOpenRecords[currentGUID])) {
						currentGUID = nestingVisibilityResult.recordsClosed[currentGUID].getParentRecord();
						if (currentGUID) {
							currentGUID = currentGUID.$internals.metaProperties.meta.guid;
						}
					}
					if (!currentGUID) {
						currentGUID = selectedRecordGUID;
					}
					// At this point, we have a particular cell that we want to select, so select it
					this.$pubsub.modules.tableNavigation.pub("selectRecordField", {
						recordGUID: currentGUID,
						fieldGUID: selectedFieldGUID
					});
				}
			}
			return new ActionResult(ACTION_STATE.EXECUTED, function canRevert() {
				return true;
			}, records, !visible);
		},
		revert: "changeNestingVisibility"
	},
	/**
	 * Set the particular nesting visibility by level
	 */
	changeNestingLevelVisibility: {
		/**
		 * Set the nesting visibility of records by a particular nesting level to show
		 * @param {List} recordsByLevel List of all records by a particular level
		 * @param {Object} levelsInfo Info object for all records
		 * @param {Number} level Level integer to open to
		 * @returns {ActionResult} Execution and reversion details for this particular action
		 */
		execute(gridSelection, recordsByLevel, levelsInfo, level) {
			// Get a handle to the previous records
			const vueComponent = this;
			const dataStore = this.$root.systemState.applicationData.activeWorkbook.dataStore;
			const sheetView = this.$root.systemState.applicationData.activeWorkbook.sheetView;
			const previousRecords = sheetView.getNestingDisclosureArray();
			// Set the raw disclosures
			const recordsOpen = nestingHelpers.setNestingVisibilityByLevel(this, recordsByLevel, levelsInfo, level);
			// Ensure that something in the grid is selected, if we came from a starting point where the grid was selected
			if (gridSelection) {
				const selectedCell = gridSelection.selectedCell;
				const selectedRecordGUID = selectedCell ? selectedCell.recordGUID : null;
				const selectedFieldGUID = selectedCell ? selectedCell.fieldGUID : null;
				let current = dataStore.getRecordByGUID(selectedRecordGUID);
				while (!recordsOpen[current.$internals.metaProperties.meta.guid]) {
					current = current.getParentRecord();
				}
				// At this point, we have a particular cell that we want to select, so select it
				this.$nextTick(() => { // Wait just a second, for reasons -____- (idk, but this actually works better)
					vueComponent.$pubsub.modules.tableNavigation.pub("selectRecordField", {
						recordGUID: current.$internals.metaProperties.meta.guid,
						fieldGUID: selectedFieldGUID
					});
				});
			}
			// Construct an inverse operation action
			return new ActionResult(ACTION_STATE.EXECUTED, function canRevert() {
				return true;
			}, previousRecords);
		},
		revert: "_setRawNestingLevelRecords"
	},
	/**
	 * Set the raw nesting level records
	 */
	_setRawNestingLevelRecords: {
		/**
		 * Set the raw nesting level by a list of records
		 * @param {List} records Raw list of records by guids
		 * @returns
		 */
		execute(records) {
			// Get a handle to the previous records
			this.$root.systemState.applicationData.activeWorkbook.sheetView;
			const previousRecords = sheetView.getNestingDisclosureArray();
			// Set the raw disclosures
			sheetView.setNestingDisclosureArray(records);
			// Construct a inverse operation action
			return new ActionResult(ACTION_STATE.EXECUTED, function canRevert() {
				return true;
			}, previousRecords);
		},
		revert: "_setRawNestingLevelRecords"
	},
	/**
	 * Move the grid selection relative to where it currently is
	 */
	gridMoveRelatively: {
		/**
		 * Change the current selection or highlight range based on the relatively selected cell
		 * @param {List} displayRecords List of all records displayed in the grid
		 * @param {List} sortedFields List of all fields displayed in the grid
		 * @param {Object} gridSelection Grid selection object instance
		 * @param {Object} locationChangeVector Object identifying the change direction vector
		 * @param {Boolean} shouldHighlight Should we just select, or highlight in between?
		 * @param {Boolean} allowNewRecordCreation Should we create a new record row?
		 */
		execute(displayRecords, sortedFields, gridSelection, locationChangeVector, shouldHighlight, allowNewRecordCreation, highlightRangeChangeInitiator) {
			const relativeRecordGUID = gridSelection.selectedCell.recordGUID;
			const relativeFieldGUID = gridSelection.selectedCell.fieldGUID;
			selectionHelpers.moveRelatively(this, displayRecords, sortedFields, gridSelection, relativeRecordGUID, relativeFieldGUID, locationChangeVector, shouldHighlight, allowNewRecordCreation, highlightRangeChangeInitiator)
			return new ActionResult(ACTION_STATE.EXECUTED, function canRevert() {
				return false;
			});
		},
		revert: "gridMoveRelatively"
	},
	/**
	 * Regardless of where the grid selection is, just move the selection
	 */
	gridMoveExplicitly: {
		/**
		 * Change the current selection or highlight range based on the relatively selected cell
		 * @param {List} displayRecords List of all records displayed in the grid
		 * @param {List} sortedFields List of all fields displayed in the grid
		 * @param {Object} gridSelection Grid selection object instance
		 * @param {Object} relativeRecordGUID Starting record guid
		 * @param {Object} relativeFieldGUID Starting field guid
		 * @param {Object} locationChangeVector Object identifying the change direction vector
		 * @param {Boolean} shouldHighlight Should we just select, or highlight in between?
		 * @param {Boolean} allowNewRecordCreation Should we create a new record row?
		 */
		execute(displayRecords, sortedFields, gridSelection, relativeRecordGUID, relativeFieldGUID, locationChangeVector, shouldHighlight, allowNewRecordCreation, highlightRangeChangeInitiator) {
			selectionHelpers.moveRelatively(this, displayRecords, sortedFields, gridSelection, relativeRecordGUID, relativeFieldGUID, locationChangeVector, shouldHighlight, allowNewRecordCreation, highlightRangeChangeInitiator)
			return new ActionResult(ACTION_STATE.EXECUTED, function canRevert() {
				return false;
			});
		},
		revert: "gridMoveExplicitly"
	},
	/**
	 * Create a new record
	 */
	createRecord: {
		/**
		 * Create a brand new record at a particular location
		 * @param {Number} index Start index of where to place the new record
		 * @param {Object} parentRecord Parent record object GUID
		 * @returns {ActionResult} Execution and reversion details for this particular action
		 */
		execute(recordName="", initialFieldValues={}, index, parentRecord, onComplete) {
			const dataStore = this.$root.systemState.applicationData.activeWorkbook.dataStore;
			const sheetView = this.$root.systemState.applicationData.activeWorkbook.sheetView;
			const licenseManager = this.$root.systemState.licenseManager;
			const vueComponent = this;
			if (dataStore.hasPermission("record_addNew")) {
				licenseManager.verifyProvisionedEntitlementAvailability("Workbook.Records", 1, function withEntitlement(hasEntitlement) {
					if (hasEntitlement) {
						function onRecordCreated(createdRecord) {
							if (onComplete) {
								onComplete(createdRecord);
							}

							vueComponent.$pubsub.modules.UI.pub('actionTaken', {
								actionClass: 'records',
								action: 'addedBlankRecord',
								details: {
									recordGUID: createdRecord.$internals.metaProperties.meta.guid
								}
							});
						}
						if (!index && !!parentRecord) {
							// Simply call our insertNestedRecord pubsub
							vueComponent.$pubsub.modules.records.pub("insertNestedRecord", {
								parentRecord,
								onComplete: onRecordCreated
							});
							// Ensure that the record is fully open
							sheetView.ensureRecordChildrenVisibleInHierarchy(parentRecord);
						} else if (index) {
							// Call our insertRecordAtIndex pubsub
							vueComponent.$pubsub.modules.records.pub("insertRecordAtIndex", {
								index,
								parentRecord,
								onComplete: onRecordCreated
							});
						} else {
							// Just create a record at the end
							const createdRecord = dataStore.addNewRecord(recordName, initialFieldValues);

							onRecordCreated(createdRecord);
						}
					} else {
						vueComponent.$pubsub.modules.upgrade.pub("openModal", {
							flowName: "entitlementExhausted",
							attributes: {
								entitlementIdentifier: "Workbook.Records"
							}
						});
					}
				});
				return new ActionResult(ACTION_STATE.EXECUTED, function canRevert() {
					return false;
				});
			} else {
				return new ActionResult(ACTION_STATE.EXECUTE_FAILED, function canRevert() {
					return false;
				});
			}
		},
		revert: "createRecord"
	}
}

// gridSelectionInstance,

/**
 * displayRecords
 * sortedFields
 *
 * vueComponent: this
 * selectedCell: gridSelection.selectedCell || null
 * highlightedRange: gridSelection.highlightedRanges[0] || null
 * focusedElementCell: document.activeElement || null
 * focusedElementCell: GridCommon.getCellFromEventElement(document.activeElement),
 */